/**
 * media upload service (wd-media-upload.service.ts).
 *
 * @version
 * 4.71.0 OAUI-693 Tidy and remove duplicated code
 * 4.71.0 OAUI-684 Improve shared code and add interfaces
 * 4.63.0 DEVOPS-715 order-admin-ui: setup new repo without vertical slicing
 * 4.62.0 OAUI-654 Remove calls to getOrderCrudApiRouting
 * 4.37.0 OAUI-527 Slowdowns in order modals
 * 4.36.0 OAUI-519 Add / Edit Adjustments
 * 4.36.0 OAUI-515 Add / Edit / Remove Order Messages
 * 4.36.0 OAUI-504 Refactor how order messages are fetched from API
 * 4.36.0 OAUI-516 Order caching issue on new order details page
 * 4.36.0 OAUI-510 Add / Edit / Remove Attachments
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BcxBytesPipe } from '../../pipes/bytes/bytes.pipe';
import { BehaviorSubject } from 'rxjs';
import { WdLocalSharedFunctionsService } from '../shared/wd-local-shared-functions.service';
import { IBcxOauth2HttpService } from '../../interfaces/generic/generic-interfaces';

@Injectable({
  providedIn: 'root',
})
export class WdMediaUploadService {
  // Booleans
  isRemovingFile = new BehaviorSubject(false); // Removing Single File
  isUploadingFiles = new BehaviorSubject(false); // Uploading Files

  // File Limits
  maxFileSize = 8388608; // 8 MB to bytes
  maxFileUploads = 20; // 20 File Limit
  maxFilePostSize = 8388608; // 8 MB to bytes

  /**
   * Constructor
   * @since 4.36.0
   */
  constructor(
    private bytesPipe: BcxBytesPipe,
    private wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    private wdTranslate: TranslateService,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject('wdOauth2HttpService')
    public wdOauth2HttpService: IBcxOauth2HttpService,
  ) {
    // Set Limits
    this.setLimits();
  }

  /**
   * Append Uploaded Files to Form
   * @since 4.36.0
   */
  private static appendUploadedFilesToForm(files: Array<any>, formData: any) {
    const results = [];

    for (let i = 0; i < files.length; i++) {
      formData.append('file' + i, files[i]);
      results.push(files[i].description);
    }

    return results;
  }

  /**
   * Build Post Form Data
   * @since 4.36.0
   */
  private static buildPostFormData(
    orderIndex: string,
    files: Array<any>,
    formData: any,
  ) {
    const result = new FormData();
    const parsedFormData = formData ? formData : {};
    parsedFormData.attachmentDescriptions = WdMediaUploadService.appendUploadedFilesToForm(
      files,
      result,
    );

    result.append('model', JSON.stringify(parsedFormData));
    result.append('orderIndex', orderIndex);

    return result;
  }

  /**
   * Remove Attached Media File
   * @since 4.36.0
   */
  async removeAttachedMediaFile(
    orderIndex: string,
    fileIndex: string,
  ): Promise<any> {
    // Toggle Loader
    this.isRemovingFile.next(true);
    try {
      // Set Path
      const path = `orders/media/${fileIndex}?orderIndex=${orderIndex}`;
      return await this.wdOauth2HttpService.delete(
        `${this.wdLocalSharedFunctions.orderingApiUrl}${path}`,
      );
    } finally {
      // Toggle Loader
      this.isRemovingFile.next(false);
    }
  }

  /**
   * Set Limits
   * @since 4.36.0
   */
  public setUploadQueue(files: Array<any>): Array<any> {
    const results = [];
    let currentPostSize = 0;

    // Set file upload queue
    for (let i = 0; i < files.length; i++) {
      const fileName = files[i].name;
      const fileSize = files[i].size;
      const potentialPostSize = currentPostSize + fileSize;

      // Files limit exceeded
      if (results.length < this.maxFileUploads) {
        // Individual files size exceeded
        if (fileSize <= this.maxFileSize) {
          // Maximum post size exceeded
          if (potentialPostSize <= this.maxFilePostSize) {
            currentPostSize += fileSize;
            results.push(files[i]);
          } else {
            this.onPostSizeError(fileName);
          }
        } else {
          this.onFileSizeError(fileName);
        }
      } else {
        this.onFileLimitError(fileName);
      }
    }

    return results;
  }

  /**
   * Update Saved Files
   * @since 4.36.0
   */
  async updateSavedFiles(orderIndex: string, file: any): Promise<any> {
    // Toggle Loader
    this.isUploadingFiles.next(true);
    try {
      // Set Path
      const path = `orders/media/${file.index}`;
      // Update saved files
      return await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderingApiUrl}${path}`,
        { orderIndex: orderIndex, description: file.description },
      );
    } finally {
      // Toggle Loader
      this.isUploadingFiles.next(false);
    }
  }

  /**
   * Upload Pending Files
   * @since 4.36.0
   */
  async uploadPendingFiles(
    orderIndex: string,
    files: Array<any>,
    formData: any,
  ): Promise<any> {
    // Toggle Loader
    this.isUploadingFiles.next(true);
    try {
      // Set Path
      const path = 'orders/media';
      const postFormData = WdMediaUploadService.buildPostFormData(
        orderIndex,
        files,
        formData,
      );
      // POST - with additional header (Required when uploading files)
      this.wdOauth2HttpService.addHeader('Content-Type', undefined);
      // Upload pending files
      const response = await this.wdOauth2HttpService.post(
        `${this.wdLocalSharedFunctions.orderingApiUrl}${path}`,
        postFormData,
      );
      // Delete Header (otherwise subsequent api calls will fail, e.g. update order details)
      this.wdOauth2HttpService.deleteHeader('Content-Type');
      return response;
    } finally {
      // Toggle Loader
      this.isUploadingFiles.next(false);
    }
  }

  /**
   * On File Limit Error
   * @since 4.36.0
   */
  private onFileLimitError(fileName: string) {
    const errMsg = this.wdTranslate.instant('labels.error');
    const errDescription = this.wdTranslate.instant('errors.files.limit', {
      file: fileName,
      count: this.maxFileUploads,
    });
    this.wdErrorService.userError(errMsg, errDescription);
  }

  /**
   * On File Size Error
   * @since 4.36.0
   */
  private onFileSizeError(fileName: string) {
    const errMsg = this.wdTranslate.instant('labels.error');
    const maxFileSize = this.bytesPipe.transform(this.maxFileSize);
    const errDescription = this.wdTranslate.instant('errors.files.size', {
      file: fileName,
      count: maxFileSize,
    });
    this.wdErrorService.userError(errMsg, errDescription);
  }

  /**
   * On Post Size Error
   * @since 4.36.0
   */
  private onPostSizeError(fileName: string) {
    const errMsg = this.wdTranslate.instant('labels.error');
    const maxFilePostSize = this.bytesPipe.transform(this.maxFilePostSize);
    const errDescription = this.wdTranslate.instant('errors.files.exceeded', {
      file: fileName,
      count: maxFilePostSize,
    });
    this.wdErrorService.userError(errMsg, errDescription);
  }

  /**
   * Set Limits
   * @since 4.36.0
   */
  private setLimits() {
    // Set Limits
    const maxFileSize = this.wdLocalSharedFunctions.limits.maxFileSize;
    const maxFileUploads = this.wdLocalSharedFunctions.limits.maxFileUploads;
    const maxFilePostSize = this.wdLocalSharedFunctions.limits.maxFilePostSize;
    // Set File Defaults - Size in MB
    if (maxFileSize) {
      this.maxFileSize = maxFileSize;
    }
    if (maxFileUploads) {
      this.maxFileUploads = maxFileUploads;
    }
    if (maxFilePostSize) {
      this.maxFilePostSize = maxFilePostSize;
    }
  }
}
